import React, { useState } from 'react';
import OTPVerification from './OTPVerification';
import { useNotificationContext } from '../context/NotificationContext';
import { authService } from '../services/authService';
import '../styles/AuthModals.css';

interface LoginData {
  email: string;
  phone: string;
  password: string;
}

interface LoginWithOTPData {
  phone: string;
}

interface SignupData {
  name: string;
  email: string;
  phone: string;
  address: string;
  password: string;
  confirmPassword: string;
}

interface AuthModalsProps {
  showLogin: boolean;
  showSignup: boolean;
  onCloseLogin: () => void;
  onCloseSignup: () => void;
  onOpenSignup: () => void;
  onOpenLogin: () => void;
}

const AuthModals: React.FC<AuthModalsProps> = ({
  showLogin,
  showSignup,
  onCloseLogin,
  onCloseSignup,
  onOpenSignup,
  onOpenLogin
}) => {
  const { showSuccess, showError, showWarning } = useNotificationContext();
  const [loginData, setLoginData] = useState<LoginData>({ email: '', phone: '', password: '' });
  const [loginOTPData, setLoginOTPData] = useState<LoginWithOTPData>({ phone: '' });
  const [signupData, setSignupData] = useState<SignupData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [signupStep, setSignupStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showLoginOTPModal, setShowLoginOTPModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp'>('password');

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginOTPData(prev => ({ ...prev, [name]: value }));
  };

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSignupData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('http://localhost:5000/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store tokens and user data using AuthService methods
        authService.setAuthToken(result.data.tokens.accessToken);
        authService.setRefreshToken(result.data.tokens.refreshToken);
        authService.setUser(result.data.user);
        
        showSuccess('Login successful!');
        onCloseLogin();
        // Reset form
        setLoginData({ email: '', phone: '', password: '' });
        
        // Reload page to update auth state
        window.location.reload();
      } else {
        const errorMsg = result.error || result.message || 'Login failed. Please try again.';
        console.error('Backend login error:', errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Network error. Please check your connection and try again.';
      showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('http://localhost:5000/api/v1/auth/login-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: loginOTPData.phone
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowLoginOTPModal(true);
        showSuccess('OTP sent to your phone number');
      } else {
        const errorMsg = result.error || result.message || 'Failed to send OTP. Please try again.';
        console.error('Backend OTP error:', errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('OTP login error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Network error. Please check your connection and try again.';
      showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (signupData.password !== signupData.confirmPassword) {
      showWarning('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      // Call signup API
      const response = await fetch('http://localhost:5000/api/v1/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: signupData.name,
          email: signupData.email,
          phone: signupData.phone,
          address: signupData.address,
          password: signupData.password
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Show OTP verification modal
        setShowOTPModal(true);
        showSuccess('Account created! Please verify your phone number.');
      } else {
        const errorMsg = result.error || result.message || 'Signup failed. Please try again.';
        console.error('Backend signup error:', errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Network error. Please check your connection and try again.';
      showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSuccess = (data?: any) => {
    showSuccess('Registration successful! Welcome to WeLegal!');
    setShowOTPModal(false);
    onCloseSignup();
    // Reset form
    setSignupData({
      name: '',
      email: '',
      phone: '',
      address: '',
      password: '',
      confirmPassword: ''
    });
    
    // Reload page to update auth state (tokens were already stored during signup)
    window.location.reload();
  };

  const handleOTPLoginSuccess = (data?: any) => {
    if (data && data.user && data.tokens) {
      // Store tokens and user data using AuthService methods
      authService.setAuthToken(data.tokens.accessToken);
      authService.setRefreshToken(data.tokens.refreshToken);
      authService.setUser(data.user);
      
      showSuccess('Login successful! Welcome back!');
      setShowLoginOTPModal(false);
      onCloseLogin();
      // Reset form
      setLoginOTPData({ phone: '' });
      
      // Reload page to update auth state
      window.location.reload();
    } else {
      showError('Login successful but failed to store authentication data. Please try logging in again.');
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/v1/auth/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: signupData.phone,
          type: 'signup'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        showSuccess('OTP sent successfully!');
      } else {
        showError(result.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      showError('Failed to resend OTP. Please try again.');
    }
  };

  const handleResendLoginOTP = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/v1/auth/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: loginOTPData.phone,
          type: 'login'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        showSuccess('OTP sent successfully!');
      } else {
        showError(result.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      showError('Failed to resend OTP. Please try again.');
    }
  };

  const switchToSignup = () => {
    onCloseLogin();
    onOpenSignup();
  };

  const switchToLogin = () => {
    onCloseSignup();
    onOpenLogin();
  };

  return (
    <>
      {/* Login Modal */}
      {showLogin && (
        <div className="modal-overlay">
          <div className="modal-content auth-modal">
            <button className="close-btn" onClick={onCloseLogin}>×</button>
            <h2>Login to WeLegal</h2>
            
            {/* Login Method Toggle */}
            <div className="login-method-toggle">
              <button 
                type="button" 
                className={`method-btn ${loginMethod === 'password' ? 'active' : ''}`}
                onClick={() => setLoginMethod('password')}
              >
                Login with Password
              </button>
              <button 
                type="button" 
                className={`method-btn ${loginMethod === 'otp' ? 'active' : ''}`}
                onClick={() => setLoginMethod('otp')}
              >
                Login with OTP
              </button>
            </div>

            {/* Password Login Form */}
            {loginMethod === 'password' && (
              <form onSubmit={handleLoginSubmit} className="auth-form">
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    required
                  />
                </div>
                <button 
                  type="submit" 
                  className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>
            )}

            {/* OTP Login Form */}
            {loginMethod === 'otp' && (
              <form onSubmit={handleOTPLoginSubmit} className="auth-form">
                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Mobile Number"
                    value={loginOTPData.phone}
                    onChange={handleLoginOTPChange}
                    pattern="[0-9]{10}"
                    required
                  />
                </div>
                <button 
                  type="submit" 
                  className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </form>
            )}

            <p className="auth-switch">
              Don't have an account? 
              <button type="button" onClick={switchToSignup} className="link-btn">Sign Up</button>
            </p>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignup && (
        <div className="modal-overlay">
          <div className="modal-content auth-modal">
            <button className="close-btn" onClick={onCloseSignup}>×</button>
            
            <h2>Sign Up for WeLegal</h2>
            <form onSubmit={handleSignupSubmit} className="auth-form">
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={signupData.name}
                  onChange={handleSignupChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Mobile Number"
                  value={signupData.phone}
                  onChange={handleSignupChange}
                  pattern="[0-9]{10}"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={signupData.email}
                  onChange={handleSignupChange}
                  required
                />
              </div>
              <div className="form-group">
                <textarea
                  name="address"
                  placeholder="Address"
                  value={signupData.address}
                  onChange={handleSignupChange}
                  rows={3}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={signupData.password}
                  onChange={handleSignupChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={signupData.confirmPassword}
                  onChange={handleSignupChange}
                  required
                />
              </div>
              <button 
                type="submit" 
                className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Creating Account...
                  </>
                ) : (
                  'Create Account & Send OTP'
                )}
              </button>
              <p className="auth-switch">
                Already have an account? 
                <button type="button" onClick={switchToLogin} className="link-btn">Login</button>
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Signup OTP Verification Modal */}
      <OTPVerification
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        phone={signupData.phone}
        onVerificationSuccess={handleOTPSuccess}
        onResendOTP={handleResendOTP}
        type="signup"
      />

      {/* Login OTP Verification Modal */}
      <OTPVerification
        isOpen={showLoginOTPModal}
        onClose={() => setShowLoginOTPModal(false)}
        phone={loginOTPData.phone}
        onVerificationSuccess={handleOTPLoginSuccess}
        onResendOTP={handleResendLoginOTP}
        type="login"
      />
    </>
  );
};

export default AuthModals;